/**
 * ExpensesStep — why the money side is in here at all. ONB-5, skippable.
 *
 * Every other screen in this flow is about work going out. This one is
 * about what it costs, and it exists because the studios who need it
 * most are the ones who won't go looking for it: the number that
 * matters to a photographer is not revenue, it is what's left after
 * the second shooter, the album and the mileage.
 *
 * Nothing to fill in. A studio cannot meaningfully categorise expenses
 * they haven't had yet, so this screen explains and moves on — the one
 * choice is whether Orbit should pre-load the categories their craft
 * usually needs, which saves a real chore later and costs nothing if
 * they never use them.
 */
import { cn } from '@/lib/utils';
import { StepIntro } from './StepIntro';

/** What a photo/video business actually writes off, by the name a
 *  studio would use rather than an accountant's. */
export const STARTER_EXPENSE_CATEGORIES = [
  'Gear & rentals',
  'Second shooters',
  'Albums & prints',
  'Travel & mileage',
  'Software',
  'Marketing',
];

export function ExpensesStep({
  seedCategories,
  onSeedCategories,
  disabled = false,
}: {
  seedCategories: boolean;
  onSeedCategories: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-6">
      <StepIntro mood="thinking">
        Revenue is the number you quote. Profit is the number you live on —
        Orbit tracks what a shoot costs so it can tell you the difference.
      </StepIntro>

      <div className="rounded-2xl border border-[color:var(--g-line)] bg-[color:var(--g-fill)] p-5">
        <ul className="space-y-2.5 text-sm leading-relaxed text-[color:var(--g-mute)]">
          <li>Log an expense against a project and it lands in that project's margin.</li>
          <li>Photograph a receipt and Nova reads the amount off it.</li>
          <li>At tax time it's a export, not an evening.</li>
        </ul>
      </div>

      <div className="space-y-2">
        <p className="text-sm text-[color:var(--g-ink)]">Start with the usual categories?</p>
        <div className="flex flex-wrap gap-1.5">
          {STARTER_EXPENSE_CATEGORIES.map((c) => (
            <span
              key={c}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs transition-colors',
                seedCategories
                  ? 'border-[color:var(--g-line-strong)] bg-[color:var(--g-fill-2)] text-[color:var(--g-ink)]'
                  : 'border-[color:var(--g-line)] bg-transparent text-[color:var(--g-mute)] opacity-80',
              )}
            >
              {c}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2 pt-2">
          {[
            { v: true, label: 'Yes, set them up' },
            { v: false, label: "I'll do my own" },
          ].map((o) => (
            <button
              key={String(o.v)}
              type="button"
              disabled={disabled}
              onClick={() => onSeedCategories(o.v)}
              aria-pressed={seedCategories === o.v}
              className={cn(
                'rounded-2xl border px-4 py-3.5 text-sm transition-colors',
                seedCategories === o.v
                  ? 'border-[color:var(--g-line-strong)] bg-[color:var(--g-fill-2)] text-[color:var(--g-ink)]'
                  : 'border-[color:var(--g-line)] bg-[color:var(--g-fill)] text-[color:var(--g-mute)] hover:bg-[color:var(--g-fill-2)]',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ExpensesStep;
